/**
 * VRG-591 — the ONE function that answers "may this certificate's visitor be asked
 * for their email / their location?".
 *
 * ## Why it lives in `packages/types`, next to `activation-trigger.resolver.ts`
 *
 * Same argument as its neighbour, with a different stake. Activation resolves to
 * a BILLING moment; these two resolve to a PRIVACY act. The platform renders the
 * answer (a product page and a certificate page each show the resolved value and
 * where it came from) and the API enforces it on two public, unauthenticated
 * write paths. If the two sides re-implemented the chain, the platform could tell
 * a brand "location collection is off for this item" while the server kept
 * accepting positions for it — both internally consistent, quietly disagreeing,
 * and nothing failing. A shared pure function is the only shape in which that
 * divergence is impossible rather than merely unlikely.
 *
 * So: no imports, no I/O, no framework. Both apps call THIS.
 *
 * ## The chain
 *
 * Most specific wins; `null` at any level means "inherit from the level above".
 *
 *   certificate.collect_email
 *     ?? product.collect_email
 *     ?? certificate_trigger_settings.collect_email   (company)
 *     ?? true                                         (system default)
 *
 * `null` is never "unconfigured pending a default" — it is an active decision to
 * follow the level above. This is why neither override column carries a DB default
 * and why neither may ever be backfilled: writing today's company value onto every
 * SKU would sever inheritance permanently and invisibly, and the next time the
 * brand changed its company-wide answer nothing would follow it.
 *
 * It is also why the columns are NULLABLE BOOLEANS and not `NOT NULL DEFAULT
 * false`. Three states are required — on, off, inherit — and a two-state column
 * would collapse "inherit" into "off" on the day the migration ran, switching
 * collection off for every product in the system.
 *
 * ## THE TWO FLAGS RESOLVE INDEPENDENTLY, and that is a decision, not an accident
 *
 * Each flag walks its OWN chain. A product may pin `collect_location = false`
 * while leaving `collect_email` inheriting, and a change to the company's email
 * default will still reach that product. The alternative — treating the pair as
 * one inherited bundle, so any override at a level freezes both — was rejected:
 *
 *  - They govern different acts. Requiring an email is an access GATE (you cannot
 *    read the page without handing one over); collecting location is passive
 *    TELEMETRY taken with consent while you read. A brand that wants the gate off
 *    for a gift item has said nothing about the map.
 *  - Bundling makes a null ambiguous. Under a bundle, `collect_location = null`
 *    on a row whose `collect_email` is set would have to mean "false", because the
 *    bundle was claimed at this level — so the same null would mean "inherit" on
 *    one row and "off" on another, which is exactly the state this file exists to
 *    prevent.
 *  - The existing company layer already treats them independently — two columns,
 *    two switches on Settings → Features, two separate refusals in
 *    `LogPublicAccessUseCase` (email off refuses the request; location off strips
 *    the position and still logs the access). A bundled child of two independent
 *    parents is not a coherent model.
 *
 * ## The system default is TRUE, unlike activation's customer-favourable default
 *
 * `DEFAULT_ACTIVATION_TRIGGER` defers the billable moment because a company that
 * never opened Settings should not be billed early. These default the other way,
 * ON, and that is not an oversight — it is the pre-existing meaning of the company
 * layer, in three places at once: the columns are `NOT NULL DEFAULT true`, the
 * entity defaults both to `true`, and `CertificateAccessSettingsService` returns
 * `{true, true}` for a company with no settings row. Defaulting to `false` here
 * would not be "more private", it would make this resolver disagree with the
 * schema it reads, and would switch collection off for every company that has
 * never opened the Features page — turning an inheritance feature into a global
 * analytics outage.
 *
 * The genuinely-unknown case (a lookup THREW, so we do not know what the brand
 * chose) is a different question and is NOT this function's job — it stays in
 * `CertificateAccessSettingsService`, which fails CLOSED on it. Uncertainty fails
 * closed; a resolved, unconfigured default is ON. Those are not the same "no".
 */

/** Which level of the chain supplied the resolved value. */
export enum AccessSettingSource {
  /** Set on this individual certificate — the most specific override. */
  Certificate = "certificate",
  /** Set on the product (SKU); this certificate inherits it. */
  Product = "product",
  /** The company's Settings -> Features "Certificate access" toggle. */
  Company = "company",
  /** Nothing was set anywhere; the system default applied. */
  Default = "default",
}

export interface ResolvedAccessSetting {
  value: boolean;
  source: AccessSettingSource;
}

/**
 * ON when nobody has said otherwise. See the "system default is TRUE" section
 * above before changing this — it is load-bearing agreement with the schema, the
 * entity default, and the service's fail-direction table, not a preference.
 */
export const DEFAULT_COLLECT_EMAIL = true;
export const DEFAULT_COLLECT_LOCATION = true;

/** One level's answer for one flag. `null`/absent = inherit. */
export type AccessSettingLevels = {
  certificate?: boolean | null;
  product?: boolean | null;
  company?: boolean | null;
};

/**
 * Resolve ONE flag down the chain.
 *
 * Every level is optional and nullable because callers legitimately know
 * different amounts: a product page has no certificate level at all, and the
 * public boot payload has neither. An omitted level is treated exactly like an
 * explicit `null` — inherit — so "I do not have this level" and "this level
 * defers" never need different handling at a call site.
 *
 * `!= null` rather than a truthiness check, because `false` is a REAL answer here
 * and the single most important one to get right: a `!levels.certificate` test
 * would silently discard every "off" override and fall through to the company's
 * "on". That is the whole defect this feature exists to make impossible, so it
 * would be an unusually expensive typo.
 */
export function resolveAccessSetting(
  levels: AccessSettingLevels,
  systemDefault: boolean,
): ResolvedAccessSetting {
  if (levels.certificate != null) {
    return {value: levels.certificate, source: AccessSettingSource.Certificate};
  }
  if (levels.product != null) {
    return {value: levels.product, source: AccessSettingSource.Product};
  }
  if (levels.company != null) {
    return {value: levels.company, source: AccessSettingSource.Company};
  }
  return {value: systemDefault, source: AccessSettingSource.Default};
}

/** One level's pair of flags, as stored on a row. */
export interface AccessSettingsLevel {
  collect_email?: boolean | null;
  collect_location?: boolean | null;
}

export interface ResolvedAccessSettings {
  collectEmail: ResolvedAccessSetting;
  collectLocation: ResolvedAccessSetting;
}

/**
 * Resolve BOTH flags for one certificate, each down its own chain.
 *
 * The convenience wrapper every real call site wants — the two flags are always
 * needed together even though they resolve apart, and spelling out two
 * `resolveAccessSetting` calls at each of the ~6 call sites is how one of them
 * eventually gets the levels crossed.
 */
export function resolveAccessSettings(levels: {
  certificate?: AccessSettingsLevel | null;
  product?: AccessSettingsLevel | null;
  company?: AccessSettingsLevel | null;
}): ResolvedAccessSettings {
  return {
    collectEmail: resolveAccessSetting(
      {
        certificate: levels.certificate?.collect_email,
        product: levels.product?.collect_email,
        company: levels.company?.collect_email,
      },
      DEFAULT_COLLECT_EMAIL,
    ),
    collectLocation: resolveAccessSetting(
      {
        certificate: levels.certificate?.collect_location,
        product: levels.product?.collect_location,
        company: levels.company?.collect_location,
      },
      DEFAULT_COLLECT_LOCATION,
    ),
  };
}
